import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "@/App";
import { useTranslation } from "@/i18n/translations";
import { addonCheckout } from "@/lib/addonCheckout";

export default function PhoneAgentCard() {
  const { t } = useTranslation();
  const [st, setSt] = useState(null);
  const [avail, setAvail] = useState([]);
  const [contains, setContains] = useState("");
  const [busy, setBusy] = useState(false);
  const [voices, setVoices] = useState([]);
  const [playing, setPlaying] = useState(false);
  useEffect(() => { axios.get(`${API}/voice/voices`).then((r) => setVoices(r.data.voices || [])).catch(() => {}); }, []);
  const preview = async (voiceId) => {
    if (playing) return;
    try {
      setPlaying(true);
      const r = await axios.post(`${API}/voice/preview`, { voice_id: voiceId }, { responseType: "blob" });
      const a = new Audio(URL.createObjectURL(r.data)); a.onended = () => setPlaying(false); await a.play();
    } catch { setPlaying(false); }
  };

  const load = useCallback(() => axios.get(`${API}/phone/status`).then((r) => setSt(r.data)).catch(() => setSt(null)), []);
  useEffect(() => { load(); }, [load]);

  const search = async () => {
    setBusy(true);
    try { const r = await axios.get(`${API}/phone/available`, { params: { contains } }); setAvail(r.data.numbers); }
    catch (e) { toast.error(e.response?.data?.detail || "Twilio error"); }
    finally { setBusy(false); }
  };
  const buy = async (n) => {
    if (!window.confirm(`Подключить номер ${n} к ИИ-администратору?`)) return;
    setBusy(true);
    try { await axios.post(`${API}/phone/buy`, { phone_number: n }); toast.success("Номер подключён — ИИ отвечает на звонки"); setAvail([]); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };
  const checkout = async (type) => {
    setBusy(true);
    try {
      if (type === "phone") {
        const res = await addonCheckout("phone", { t, onAttached: () => { toast.success(t("addons.attached")); load(); } });
        if (res?.cancelled || res?.status === "attached") setBusy(false);
        return;
      }
      const r = await axios.post(`${API}/billing/addon/checkout`, { addon_type: type, qty: 1, origin_url: window.location.origin });
      if (r.data?.url) window.location.href = r.data.url;
    } catch (e) { toast.error(e.response?.data?.detail || "Error"); setBusy(false); }
  };
  const saveSettings = async (patch) => {
    try { await axios.put(`${API}/phone/settings`, patch); toast.success("Сохранено"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const resume = async () => {
    try { await axios.post(`${API}/phone/resume`); toast.success("ИИ снова отвечает на звонки"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
  };
  const release = async () => {
    if (!window.confirm("Отключить номер? Он будет возвращён, звонки перестанут обрабатываться.")) return;
    setBusy(true);
    try { await axios.delete(`${API}/phone/number`); toast.success("Номер отключён"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Error"); }
    finally { setBusy(false); }
  };

  if (!st || !st.configured) return null;
  const pct = Math.min(100, Math.round((st.minutes_used / st.minutes_limit) * 100));
  return (
    <div className="border-2 border-black rounded-lg p-4 sm:p-6 mt-4 bg-white" data-testid="phone-agent-card">
      <div className="flex items-start gap-3">
        <div className="w-12 h-12 flex items-center justify-center bg-black rounded-lg text-white text-2xl shrink-0">🤖</div>
        <div className="flex-1 min-w-0">
          <h3 className="text-lg sm:text-xl font-bold">Телефонный ИИ-администратор</h3>
          <p className="text-gray-600 text-xs sm:text-sm mt-1">
            Свой городской номер для салона. ИИ отвечает на звонки голосом, называет цены и свободные окна и сам записывает в календарь.
            Укажите номер в Google Картах и Instagram или поставьте на него переадресацию при занятости. €{st.prices.addon}/мес · 150 минут включено.
          </p>
        </div>
      </div>

      {!st.enabled && (
        <div className="mt-4 p-4 rounded-lg bg-gray-50 border-2 border-gray-200 flex flex-wrap items-center gap-3" data-testid="phone-addon-locked">
          <div className="flex-1 min-w-[220px] text-sm">
            <div className="font-bold">Телефонный ИИ-администратор — €{st.prices.addon}/мес{st.prices.addon < st.prices.addon_full && <span className="ml-2 text-xs font-semibold text-green-700 bg-green-50 rounded-full px-2 py-0.5" data-testid="phone-sub-discount">цена для подписчиков · вместо €{st.prices.addon_full}</span>}</div>
            <div className="text-xs text-gray-600">150 минут разговоров включено · свой номер · расшифровки в «Сообщениях» · минуты сверх пакета +€{st.prices.minutes100} за 100. Исходящие звонки-напоминания считаются ×2 (минута = 2 минуты пакета)</div>
          </div>
          <button type="button" onClick={() => checkout("phone")} disabled={busy} data-testid="phone-addon-buy-btn"
            className="bg-black text-white text-sm font-semibold rounded-xl px-5 py-2.5 disabled:opacity-50">Подключить за €{st.prices.addon}</button>
        </div>
      )}

      {st.enabled && st.number && (
        <div className="mt-4 space-y-3" data-testid="phone-number-active">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-2xl font-extrabold tracking-wide" data-testid="phone-number-value">{st.number.phone_number}</span>
            <span className="text-xs px-2 py-1 rounded-full bg-green-100 text-green-700">🟢 ИИ отвечает</span>
            <button type="button" onClick={release} disabled={busy} data-testid="phone-release-btn" className="ml-auto text-xs underline text-gray-500 hover:text-red-600">Отключить номер</button>
          </div>
          <div>
            <div className="flex justify-between text-xs text-gray-600 mb-1"><span>Минуты в этом месяце</span><span data-testid="phone-minutes">{st.minutes_used} / {st.minutes_limit} мин · звонков: {st.calls}</span></div>
            {st.minutes_outbound > 0 && <div className="text-[11px] text-gray-400 mb-1" data-testid="phone-minutes-split">входящие {st.minutes_inbound} мин · исходящие {st.minutes_outbound} мин (×{st.outbound_factor})</div>}
            <div className="h-2 bg-gray-200 rounded-full overflow-hidden"><div className={`h-full ${pct > 90 ? "bg-red-500" : "bg-black"}`} style={{ width: `${pct}%` }} /></div>
          </div>
          {st.number.suspended && (
            <div className="p-3 rounded-lg bg-red-50 border border-red-300 text-sm flex flex-wrap items-center gap-2" data-testid="phone-suspended">
              <span className="flex-1">⛔ Минуты закончились — ИИ не отвечает на звонки.</span>
              {st.minutes_used < st.minutes_limit && <button type="button" onClick={resume} className="text-xs font-semibold underline" data-testid="phone-resume-btn">Возобновить</button>}
            </div>
          )}
          <div className="flex flex-wrap gap-2">
            <button type="button" onClick={() => checkout("phone_minutes")} disabled={busy} data-testid="phone-minutes-buy-btn"
              className="text-xs font-semibold px-3 py-2 rounded-xl border-2 border-black bg-white hover:bg-gray-50" title="100 входящих или 50 исходящих минут">+100 минут за €{st.prices.minutes100}</button>
            <label className="flex items-center gap-2 text-xs cursor-pointer px-2">
              <input type="checkbox" checked={st.auto_topup} onChange={(e) => saveSettings({ auto_topup: e.target.checked })} data-testid="phone-auto-topup" className="accent-black w-4 h-4" />
              Автодокупка: при исчерпании минут списывать €{st.prices.minutes100} с карты подписки
            </label>
          </div>
          <div className="pt-2 grid grid-cols-1 sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-gray-700">Голос администратора</label>
              <div className="flex gap-2 mt-1">
                <select value={st.voice_id} onChange={(e) => saveSettings({ voice_id: e.target.value })} data-testid="phone-voice-select"
                  className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-black outline-none flex-1">
                  {voices.map((v) => <option key={v.voice_id} value={v.voice_id}>{v.name}{v.gender ? ` · ${v.gender}` : ""}</option>)}
                </select>
                <button type="button" onClick={() => preview(st.voice_id)} disabled={playing} data-testid="phone-voice-preview" className="text-xs font-semibold px-3 rounded-xl border-2 border-black bg-white">{playing ? "…" : "▶ Послушать"}</button>
              </div>
              <p className="text-[11px] text-gray-500 mt-1">Этим же голосом ИИ отправляет голосовые ответы в WhatsApp/Instagram (настройка в Настройках).</p>
            </div>
          <div>
            <label className="text-xs font-semibold text-gray-700">Язык разговора по умолчанию</label>
            <select value={st.language} onChange={(e) => saveSettings({ language: e.target.value })} data-testid="phone-language-select"
              className="mt-1 border-2 border-gray-200 rounded-xl px-3 py-2 text-sm bg-white focus:border-black outline-none w-full">
              {(st.languages || []).map((l) => <option key={l} value={l}>{{ ru: "Русский", fr: "Français", en: "English", tr: "Türkçe" }[l] || l}</option>)}
            </select>
            <p className="text-[11px] text-gray-500 mt-1">ИИ начинает разговор на этом языке; если звонящий говорит или просит по-английски/по-русски и т.д. — автоматически переходит на его язык.</p>
          </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 pt-2">
            <div>
              <label className="text-xs font-semibold text-gray-700">Перевод на человека — телефон мастера/владельца</label>
              <input defaultValue={st.transfer_number} placeholder="+33612345678" data-testid="phone-transfer-input"
                onBlur={(e) => e.target.value !== st.transfer_number && saveSettings({ transfer_number: e.target.value })}
                className="mt-1 w-full border-2 border-gray-200 rounded-xl px-3 py-2 text-sm focus:border-black outline-none" />
              <p className="text-[11px] text-gray-500 mt-1">Если клиент просит живого человека или жалуется — ИИ переведёт звонок на этот номер.</p>
            </div>
            <label className="flex items-start gap-2 text-sm cursor-pointer p-3 rounded-xl border-2 border-gray-200">
              <input type="checkbox" checked={st.reminder_calls} onChange={(e) => saveSettings({ reminder_calls: e.target.checked })} data-testid="phone-reminder-calls" className="accent-black w-4 h-4 mt-0.5" />
              <span><span className="font-semibold">Напоминать о записи звонком</span><br /><span className="text-xs text-gray-500">За день до визита ИИ позвонит клиенту, подтвердит запись или сразу перенесёт её на свободное время. Минуты — из пакета.</span></span>
            </label>
            <label className="flex items-start gap-2 text-sm cursor-pointer p-3 rounded-xl border-2 border-gray-200">
              <input type="checkbox" checked={st.wa_followup !== false} onChange={(e) => saveSettings({ wa_followup: e.target.checked })} data-testid="phone-wa-followup" className="accent-black w-4 h-4 mt-0.5" />
              <span><span className="font-semibold">WhatsApp-визитка после звонка</span><br /><span className="text-xs text-gray-500">Если входящий звонок не закончился записью (вопрос, обрыв, пропущенный), через ~1,5 минуты клиент получит в WhatsApp короткое сообщение от салона со ссылкой на онлайн-запись. Отправляется только если у номера есть WhatsApp и подключён ваш WhatsApp. Не чаще 1 раза в сутки на номер.</span></span>
            </label>
          </div>
          <p className="text-xs text-gray-500">Расшифровки звонков появляются в «Сообщениях» (канал «Телефон»), записи — в календаре.</p>
        </div>
      )}

      {st.enabled && !st.number && (
        <div className="mt-4" data-testid="phone-pick-number">
          <div className="flex gap-2 flex-wrap">
            <input value={contains} onChange={(e) => setContains(e.target.value.replace(/\D/g, "").slice(0, 6))} placeholder="Цифры в номере (напр. 01 или 777)"
              data-testid="phone-contains-input" className="border-2 border-gray-200 rounded-xl px-3 py-2 text-sm w-full sm:w-64 focus:border-black outline-none" />
            <button type="button" onClick={search} disabled={busy} data-testid="phone-search-btn" className="bg-black text-white text-sm font-semibold rounded-xl px-4 py-2 disabled:opacity-50">
              {busy ? "…" : "Показать свободные номера"}
            </button>
          </div>
          {avail.length > 0 && (
            <div className="mt-3 grid grid-cols-1 sm:grid-cols-2 gap-2">
              {avail.map((n) => (
                <button key={n.phone_number} type="button" onClick={() => buy(n.phone_number)} disabled={busy} data-testid={`phone-buy-${n.phone_number}`}
                  className="flex items-center justify-between border-2 border-gray-200 hover:border-black rounded-xl px-3 py-2 text-sm text-left">
                  <span className="font-bold">{n.phone_number}</span><span className="text-xs text-gray-500">{n.locality} · подключить</span>
                </button>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
